// User-panel URL map — one place for every address the app links to or routes
// on, so a path is never spelled out twice.
//
// Addresses follow the product lines the sidebar is grouped by (PREVENT /
// DEFLECT / HEALTH / RECOVER, CHA1-2847). Keys still name the screen, not the
// group: a screen can be regrouped without every call site being rewritten, and
// the URL it now lives at is the only thing that changes here.
//
// Admin-panel addresses live with their own screens and are not collected here.
const RoutesPaths = {
  home: "/home",

  // Group parents. They carry no screen of their own: the sidebar's Alerts,
  // Receipt and Status rows address the group, and it opens the default child.
  prevent: {
    root: "/prevent",
    // Upsell screen for the paid add-ons, routed outside the app layout
    // (see InitComponent/index.js).
    premium_features: "/prevent/premium_features"
  },

  // PREVENT → Analytics.
  dashboard: {
    root: "/prevent/analytics",
    // The two drill-downs keep the address they have always had: CHA1-2847's map
    // does not cover them, so they were left alone rather than moved on a guess.
    alerts_by_country: "/dashboard/alerts_by_country",
    alerts_by_bank: "/dashboard/alerts_by_bank",
    // Return address for the first top-up. The backend still builds the old one
    // (chargeback_back/payments/views.py); it redirects here, query intact.
    thank_you: "/prevent/analytics/thank-you"
  },

  // PREVENT → Alerts.
  alerts: {
    all: "/prevent/all",
    _details: "/prevent/all/details",
    details: (alertId) => `/prevent/all/details/${alertId}`
  },

  credits: "/prevent/credits",

  configure: {
    root: "/prevent/configure",
    enrollment: "/prevent/configure/enrollment",
    auto_refund: "/prevent/configure/auto-refund",
    // Integrate tab — the Integrations page rendered inside Configure
    // (CHA1-2847). Tab and first sub-tab share one address.
    processors: "/prevent/configure/processors",
    subscriptions: "/prevent/configure/subscriptions",
    notifications: "/prevent/configure/notifications"
  },

  // DEFLECT → Receipt.
  receipts: {
    all: "/deflect/all",
    _configure: "/deflect/configure",
    //
    configure_profile: "/deflect/configure/profile",
    configure_profile_hash: (hash) => `/deflect/configure/profile/${hash}`,
    //
    configure_business: "/deflect/configure/business",

    // Admin (support) view of a merchant's Configure, under Business settings.
    // The BA these render for comes from the `business_account_id` query param
    // (see BusinessSettings/index.js), so every navigation between these must
    // carry the current `search` along with the path.
    admin_configure_business: "/business_settings/receipts/configure",
    admin_configure_profile: "/business_settings/receipts/configure/profile",
    admin_configure_profile_hash: (hash) => `/business_settings/receipts/configure/profile/${hash}`
  },

  health: {
    root: "/health",
    overview: "/health/overview",
    monitoring: "/health/monitoring"
  },

  perks: "/perks",

  deflect: {
    root: "/deflect",
    // Not released. A sidebar placeholder carrying a SOON badge — it never
    // navigates, so no route answers it.
    analytics: "/deflect/analytics"
  },

  // Not released either, same SOON placeholders.
  recover: {
    disputes: "/recover",
    analytics: "/recover/analytics"
  },

  settings: {
    root: "/settings",
    my_profile: "/settings/my_profile",
    appearance: "/settings/appearance",
    notifications: "/settings/notifications",
    business_accounts: "/settings/business_accounts",
    team: "/settings/team",
    billing: "/settings/billing",
    balance: "/settings/balance",
    referrals: "/settings/referrals",
    mcp: "/settings/mcp",
    developers: "/settings/developers",
    developers_webhooks: "/settings/developers/webhooks"
  }
}

// The addresses the platform used before the product-line scheme. They are not
// linked to any more, but they must keep resolving for good: they sit in e-mails
// already sent, in browser bookmarks and in links the backend still builds.
// Every one of them is answered by a redirect — see InitComponent/legacyRoutes.js.
const LegacyRoutesPaths = {
  dashboard: "/dashboard",
  dashboard_thank_you: "/dashboard/thank-you",
  alerts: "/alerts",
  alert_details: "/alerts/details",
  credits: "/credits",
  configure: "/configure",
  configure_receipts: "/configure/receipts",
  receipts: "/receipts",
  digital_receipt: "/digital-receipt",
  integrations: "/integrations",
  integrations_premium_features: "/integrations/premium_features",
  team: "/team",
  developers: "/developers",
  // Never a screen of ours — the sidebar's Help center row opened the external
  // help site, so the address has to leave the app instead of routing in it.
  help: "/help"
}

export { LegacyRoutesPaths, RoutesPaths }
